/**
 * usePublicDossier — public, unauthenticated fetch for GET /v1/verify/:token
 * when the token resolves a disclosure:"public" export.
 *
 * Mirrors useVerifyArtifact in structure. No auth header, no Firebase
 * dependency. The endpoint always returns HTTP 200; `status` carries the
 * verdict.
 *
 * Capacitor readiness:
 *   - No firebase/* imports — plain fetch.
 *   - No navigator.onLine — uses useOnlineStatus() in the consuming page.
 *   - No localStorage/sessionStorage — no caching of sensitive data.
 */

import { env } from '@/lib/env';
import type { PublicDossierResponse } from '@/types/api';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';

/** TanStack Query key factory for public dossier. */
export const publicDossierKeys = {
  forToken: (token: string) => ['publicDossier', token] as const,
};

export interface UsePublicDossierResult {
  data: PublicDossierResponse | null;
  loading: boolean;
  error: Error | null;
}

/**
 * Fetch the public dossier payload for a token.
 *
 * @param token - The dossier token from the URL (e.g. `/v/:token`). Pass
 *                null/undefined to skip fetching.
 */
export function usePublicDossier(token: string | null | undefined): UsePublicDossierResult {
  const result: UseQueryResult<PublicDossierResponse, Error> = useQuery<
    PublicDossierResponse,
    Error
  >({
    queryKey: publicDossierKeys.forToken(token ?? ''),
    queryFn: async (): Promise<PublicDossierResponse> => {
      const response = await fetch(`${env.VITE_GG_API_BASE_URL}/v1/verify/${token}`, {
        method: 'GET',
        headers: { Accept: 'application/json' },
        // No Authorization header — public endpoint.
      });

      if (response.status === 429) {
        throw new Error('Too many requests. Please wait a moment and try again.');
      }

      if (!response.ok) {
        throw new Error(`Unexpected response ${response.status}: ${response.statusText}`);
      }

      return (await response.json()) as PublicDossierResponse;
    },
    enabled: Boolean(token),
    // Public dossier payloads are stable; cache for 5 minutes.
    staleTime: 5 * 60_000,
    retry: 1,
    retryDelay: 1500,
  });

  return {
    data: result.data ?? null,
    loading: result.isLoading,
    error: result.error ?? null,
  };
}
